import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { OfferHistory, ObjectType } from '@/@types/graphql-types';
import { useStreamingOffersHistory } from '@/features/streaming-offers-history/composables/useStreamingOffersHistory';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';

type SlimOfferHistory = Omit<OfferHistory, 'package'> & {
	package: Partial<OfferHistory['package']>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'StreamingOffersHistory',
  props: {
    titleName: null,
    titleObjectId: null,
    titleObjectType: null,
    offersHistory: null,
    country: null,
    language: null
  },
  setup(__props: any) {

const props = __props as {
	titleName?: string;
	titleObjectId: number;
	titleObjectType: ObjectType;
	offersHistory: SlimOfferHistory[];
	country: string;
	language: string;
};



const { todayDate } = usePersistedTodaysDate();
const { dateFormatter, spinningText, sortedOffersHistory } = useStreamingOffersHistory({
	offersHistory: () => props.offersHistory,
	titleName: () => props.titleName ?? '',
});

const titleContext = computed(() => new SnowplowTitleContextGraphql(props.titleObjectId, props.titleObjectType));

const onVisibleCallback = {
	callback: (isVisible: boolean) => {
		if (isVisible) {
			TrackingHelper.trackEvent(
				'stream_offer_history_feature',
				{
					action: 'impression',
					label: 'stream_offer_history',
					nonInteraction: true,
				},
				[titleContext.value]
			);
		}
	},
};

// Get the earliest and latest dates an offer was added/removed for the title
const dateBounds = computed(() => {
	const dates = props.offersHistory
		.flatMap(({ dateRanges }) => dateRanges)
		.flatMap(({ start, end }) => [new Date(start).getTime(), new Date(end).getTime()]);

	return {
		earliestStart: new Date(Math.min(...dates)),
		latestEnd: new Date(Math.max(...dates)),
	};
});

const timelineDates = computed(() => {
	const earliestStart = dateBounds.value.earliestStart;
	const latestEnd = dateBounds.value.latestEnd;

	if (!earliestStart || !latestEnd || !isFinite(earliestStart.getTime()) || !isFinite(latestEnd.getTime())) {
		return [];
	}

	const earliestYear = earliestStart.getFullYear();
	const latestYear = latestEnd.getFullYear();

	let startYear, startMonth, endYear, endMonth;

	if (earliestYear === latestYear) {
		// If the full timespan is within the same year, set the timeline to only show that year
		startYear = earliestYear;
		startMonth = 0;
		endYear = latestYear + 1;
		endMonth = 0;
	} else {
		const currentDate = todayDate.value;
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth();
		const oneYearAfterLatest = latestYear + 1;

		startYear = earliestYear;
		startMonth = 0;

		if (currentYear < oneYearAfterLatest) {
			endYear = currentYear;
			endMonth = currentMonth;
		} else {
			endYear = oneYearAfterLatest;
			endMonth = latestEnd.getMonth();
		}
	}

	// Calculate total months
	const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

	if (totalMonths <= 0 || totalMonths > 1200) {
		// Safety check: prevent excessive array sizes (> 100 years)
		return [];
	}

	const monthsArray = new Array(totalMonths);

	// Generate month strings
	let currentYear = startYear;
	let currentMonth = startMonth;

	for (let i = 0; i < totalMonths; i++) {
		monthsArray[i] = `${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`;
		currentMonth++;
		if (currentMonth > 11) {
			currentMonth = 0;
			currentYear++;
		}
	}

	return monthsArray;
});

// Pre-render all rows with their data points to avoid nested v-for calculations
const renderedRows = computed(() => {
	const dates = timelineDates.value;
	if (dates.length === 0) return [];

	const rows = [];

	for (const item of sortedOffersHistory.value) {
		const dataPoints = [];

		for (const date of dates) {
			const [month, year] = date.split('/');
			const isoString = new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toISOString().slice(0, 7);

			let isStart = false;
			let isEnd = false;
			let isInclusive = false;
			let matchingRange: { start: string; end: string } | null = null;

			for (const range of item.dateRanges) {
				const itemDateFrom = range.start.slice(0, 7);
				const itemDateTo = range.end.slice(0, 7);

				if (itemDateFrom <= isoString && itemDateTo >= isoString) {
					if (!matchingRange) matchingRange = range;
					if (itemDateFrom === isoString) isStart = true;
					if (itemDateTo === isoString) isEnd = true;
					if (itemDateFrom < isoString && itemDateTo > isoString) isInclusive = true;
				}
			}

			const classes = {
				'data__point--start': isStart,
				'data__point--end': isEnd,
				'data__point--inclusive': isInclusive,
			};

			let tooltip = '';
			if (matchingRange) {
				const startDate = dateFormatter.format(new Date(matchingRange.start));
				const endDate = dateFormatter.format(new Date(matchingRange.end));
				tooltip = `${startDate} - ${endDate}`;
			}

			dataPoints.push({ date, classes, tooltip });
		}

		rows.push({
			itemId: item.id,
			iconUrl: item.package.iconWide,
			providerShortName: item.package.shortName,
			dataPoints,
		});
	}

	return rows;
});

function formatTimelineDate(date: string) {
	const month = date.split('/')[0];
	const year = date.split('/')[1];
	return month === '01' ? year : '';
}

function isJan(date: string) {
	return date.split('/')[0] === '01';
}

return { __sfc: true,props, todayDate, dateFormatter, spinningText, sortedOffersHistory, titleContext, onVisibleCallback, dateBounds, timelineDates, renderedRows, formatTimelineDate, isJan, ProviderIcon }
}

})